import { createChatRoom, useChatRoom } from '@commenda/api';
import { Button, Icons, useClickOutside, useToggle } from '@commenda/kakashi';
import { ChatRoomType } from '@commenda/prisma';
import { ChatRoomModel } from '@commenda/utils';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { ChatRoom } from '../ChatRoom';
import {
  BookkeepingChatRoomInfo,
  ChatRoomInfo,
  ComplianceItemChatRoomInfo,
  GeneralChatRoomInfo,
  MarketplaceServiceChatRoomInfo,
  ServiceChatRoomInfo,
} from '../ChatRoom/types';

const PANEL_TRIGGER_GAP = 8;
const PANEL_WINDOW_GUTTER = 16;

export const ChatRoomTrigger = ({
  chatRoomInfo,
  hideLabel = false,
  userId,
  companyId,
  isChatClosedOverride,
  includeInternalNotes,
}: {
  chatRoomInfo: ChatRoomInfo;
  hideLabel?: boolean;
  userId: string;
  companyId: number;
  isChatClosedOverride?: boolean;
  includeInternalNotes: boolean;
}) => {
  const [chatRoomId, setChatRoomId] = useState<string | null>(
    chatRoomInfo?.chatRoomId ?? null
  );
  const chatRoomVisibility = useToggle(false);
  const { data: chatRoomResponse } = useChatRoom({ chatRoomId });

  // A thread opened after the filing was last read reaches the trigger once the filing is re-read.
  useEffect(() => {
    if (chatRoomInfo?.chatRoomId) setChatRoomId(chatRoomInfo.chatRoomId);
  }, [chatRoomInfo?.chatRoomId]);

  const handleClickOutside = useCallback(() => {
    if (chatRoomVisibility.value) {
      chatRoomVisibility.setValue(false);
    }
  }, [chatRoomVisibility]);

  const containerRef = useClickOutside<HTMLDivElement>(handleClickOutside);

  // The panel is pinned to the window under the trigger: a scroll container shorter than the
  // panel (a filing page with no tasks) would otherwise clip it. It moves up to stay on screen.
  const panelAnchorRef = useRef<HTMLDivElement>(null);
  const [panelPosition, setPanelPosition] = useState<{
    top: number;
    right: number;
  }>();

  const togglePanel = () => {
    const trigger = containerRef.current?.getBoundingClientRect();
    if (trigger)
      setPanelPosition({
        top: trigger.bottom + PANEL_TRIGGER_GAP,
        right: window.innerWidth - trigger.right,
      });
    chatRoomVisibility.toggle();
  };

  useLayoutEffect(() => {
    const panel =
      panelAnchorRef.current?.firstElementChild?.getBoundingClientRect();
    if (!chatRoomVisibility.value || !panel) return;

    const overflow = panel.bottom - (window.innerHeight - PANEL_WINDOW_GUTTER);
    if (overflow > 0)
      setPanelPosition(
        (position) =>
          position && {
            ...position,
            top: Math.max(PANEL_WINDOW_GUTTER, position.top - overflow),
          }
      );
  }, [chatRoomVisibility.value]);

  const onCreateOrThrowChatRoom = async (): Promise<string> => {
    let res: Awaited<ReturnType<typeof createChatRoom>>;
    const { chatRoomType } = chatRoomInfo;

    if (chatRoomType === ChatRoomType.SERVICE_REQUEST_THREAD) {
      const info = chatRoomInfo as ServiceChatRoomInfo;
      res = await createChatRoom({
        createChatRoomProps: {
          chatRoomType: ChatRoomType.SERVICE_REQUEST_THREAD,
          serviceRequestId: info.serviceRequestId,
          complianceId: info.complianceId,
        },
      });
    } else if (chatRoomType === ChatRoomType.MARKETPLACE_SERVICE_THREAD) {
      const info = chatRoomInfo as MarketplaceServiceChatRoomInfo;
      res = await createChatRoom({
        createChatRoomProps: {
          chatRoomType: ChatRoomType.MARKETPLACE_SERVICE_THREAD,
          marketplaceServiceId: info.marketplaceServiceId,
          companyId,
        },
        token: null,
      });
    } else if (chatRoomType === ChatRoomType.BOOKKEEPING_THREAD) {
      const info = chatRoomInfo as BookkeepingChatRoomInfo;
      res = await createChatRoom({
        createChatRoomProps: {
          chatRoomType: ChatRoomType.BOOKKEEPING_THREAD,
          corporationId: info.corporationId,
        },
        token: null,
      });
    } else if (chatRoomType === 'GENERAL_THREAD') {
      const info = chatRoomInfo as GeneralChatRoomInfo;
      res = await createChatRoom({
        createChatRoomProps: {
          chatRoomType: 'GENERAL_THREAD',
          companyId: info.companyId,
        },
        token: null,
      });
    } else {
      const info = chatRoomInfo as ComplianceItemChatRoomInfo;
      res = await createChatRoom({
        createChatRoomProps: {
          chatRoomType: ChatRoomType.COMPLIANCE_THREAD,
          complianceId: info.filingId,
          serviceRequestId: info.serviceRequestId,
        },
        token: null,
      });
    }

    if (!res.ok) throw new Error('Failed to create chat room');
    const resData = await res.json();
    const newChatRoomId = resData?.chatRoom?.id;
    setChatRoomId(newChatRoomId);
    return newChatRoomId;
  };

  const room = chatRoomResponse?.chatRoom;
  const hasUnreadMessages = room
    ? ChatRoomModel.hasUnread(room, includeInternalNotes)
    : false;

  return (
    <div className="relative" ref={containerRef}>
      {hideLabel ? (
        <Button className="p-1" onClick={togglePanel} variant="tertiary">
          <div className="relative top-0 left-0">
            <Icons.Inbox01 className="size-6" />
            {hasUnreadMessages && (
              <div className="absolute top-[-2px] right-[-2px] w-3 h-3 bg-blue-500 rounded-full border-2 border-white" />
            )}
          </div>
        </Button>
      ) : (
        <Button
          onClick={togglePanel}
          className="flex items-center gap-md"
          variant="secondary"
        >
          <div className="relative">
            <Icons.Inbox01 className="size-5" />
            {hasUnreadMessages && (
              <div className="absolute top-[-2px] right-[-2px] w-2.5 h-2.5 bg-blue-500 rounded-full border-2 border-white" />
            )}
          </div>
          Messages
        </Button>
      )}
      <div ref={panelAnchorRef} className="fixed z-50" style={panelPosition}>
        <ChatRoom
          variant="customer-only"
          open={chatRoomVisibility.value}
          chatRoomId={chatRoomId}
          onCreateOrThrowChatRoom={onCreateOrThrowChatRoom}
          userId={userId}
          companyId={companyId}
          isChatClosedOverride={isChatClosedOverride}
        />
      </div>
    </div>
  );
};
